import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import type { StreakState } from '@/api/client';

/**
 * Streak indicator with a visual progress bar to the next reward.
 *
 * Layout:
 *   • Top row: animated flame + day count
 *   • Middle: progress bar with milestone dots (3 / 7 / 14 / 30 / 60 / 100)
 *   • Bottom: next reward icon + name + days remaining
 *
 * The progress bar shows current position and the next milestone, with
 * earlier milestones lit up. Pure CSS — no charting library.
 */

// Hard-coded milestone days that match the backend `streaks.TIERS`.
// Kept here (not fetched) so the bar paints instantly with no API call.
const MILESTONES = [3, 7, 14, 30, 60, 100];

export default function StreakChip({ state }: { state: StreakState | null }) {
  const { t } = useTranslation();

  if (!state) return null;

  const { current, next_reward } = state;

  if (current === 0) {
    return (
      <p className="text-center text-[11px] uppercase tracking-widest text-gold-300/50">
        ✦ {t('streak.no_streak')}
      </p>
    );
  }

  // Flame grows slightly with streak length, caps at 30.
  const flameScale = 1 + Math.min(current, 30) / 60;
  const daysLeft = next_reward?.days_left ?? 0;
  const todayUnlocks = next_reward && daysLeft === 0;

  // Progress bar fills from 0 to the highest milestone (100). After
  // 100, just keep showing the bar fully golden — the user has won.
  const maxDay = MILESTONES[MILESTONES.length - 1];
  const fillPct = Math.min(100, (current / maxDay) * 100);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-baseline gap-2">
        <motion.span
          initial={{ scale: 0.6, rotate: -10 }}
          animate={{ scale: flameScale, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 14 }}
          className="text-2xl"
          style={{ filter: 'drop-shadow(0 0 6px rgba(232, 195, 86, 0.6))' }}
        >
          🔥
        </motion.span>
        <span className="font-serif text-lg text-gold-200">
          {t('streak.days', { count: current })}
        </span>
      </div>

      {/* Progress bar with milestone dots */}
      <div className="relative w-full max-w-[300px]">
        {/* Track */}
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-night-900/60
                        border border-gold-500/15">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r
                       from-gold-500/70 via-gold-400 to-gold-200"
            initial={{ width: 0 }}
            animate={{ width: `${fillPct}%` }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            style={{ filter: 'drop-shadow(0 0 4px rgba(232,195,86,0.5))' }}
          />
        </div>

        {/* Milestone dots */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full pointer-events-none">
          {MILESTONES.map((day) => {
            const pos = (day / maxDay) * 100;
            const reached = current >= day;
            return (
              <div
                key={day}
                className="absolute -translate-x-1/2 -translate-y-1/2 top-0"
                style={{ left: `${pos}%` }}
              >
                <div
                  className={`h-2.5 w-2.5 rounded-full border transition
                    ${reached
                      ? 'border-gold-300 bg-gold-400 shadow-[0_0_6px_rgba(232,195,86,0.7)]'
                      : 'border-gold-500/40 bg-night-800'}`}
                />
                <span className={`absolute left-1/2 mt-1.5 -translate-x-1/2
                                  text-[8px] uppercase tracking-widest
                                  ${reached ? 'text-gold-400/80' : 'text-gold-500/40'}`}>
                  {day}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next reward callout */}
      {next_reward && (
        <div className="mt-3 flex max-w-[280px] flex-col items-center text-center
                        rounded-full border border-gold-500/25 bg-night-800/40
                        px-3 py-1.5">
          <span className="text-[9px] uppercase tracking-widest text-gold-500/60">
            {t('streak.next_label')}
          </span>
          <span className="mt-0.5 flex items-baseline gap-1.5 font-serif text-sm text-gold-200">
            <span>{next_reward.icon}</span>
            <span>{next_reward.title}</span>
          </span>
          <span className="text-[10px] italic text-gold-300/60">
            {todayUnlocks
              ? t('streak.unlocks_today')
              : t('streak.in_days', { count: daysLeft })}
          </span>
        </div>
      )}
    </div>
  );
}
